import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  claimIdentity,
  fetchProfile,
  markOnboarded,
  removeIdentity,
  type ProfileView,
} from '../api/profiles';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { IdentityPanel } from './IdentityPanel';
import { TesterStart } from './TesterStart';
import { useMeta } from './MetaContext';
import { localReturnPath, nextQuery } from './returnPath';
import { errorText } from './useSessionLive';
import { Wordmark } from './Wordmark';

/**
 * The first sign-in's two questions (Steam, Discord), asked once: answered
 * or skipped, the account is marked and the page never comes back by itself.
 * It is the profile's identity panel with a welcome around it: what the
 * account can do here, by its roles, and one way on (the owner, 2026-10-12:
 * two buttons that did the same thing were one too many). The button is named
 * for what it is: "Continua" once an identity is linked, "Ara no, continua"
 * while none is.
 */
export function Welcome() {
  const { meta } = useMeta();
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // Where the person was going when the platform asked them to register: on from here once done.
  const next = localReturnPath(search.get('next'));
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me.authenticated || !me.handle) return;
    fetchProfile(me.handle)
      .then(setProfile)
      .catch((e) => setError(errorText(e)));
  }, [me.authenticated, me.handle]);

  async function done() {
    try {
      await markOnboarded();
      await refresh();
      navigate(next ?? '/', { replace: true });
    } catch (e) {
      setError(errorText(e));
    }
  }

  if (!me.authenticated) {
    return (
      <main className="auth">
        <div className="wordmark" role="img" aria-label={meta.name}>
          <Wordmark name={meta.name} />
        </div>
        <p className="muted">
          <Link to="/signin">{ca.signIn}</Link>
        </p>
      </main>
    );
  }

  // What the page knows newest: the profile it loaded (a claim saved a moment ago is there) and,
  // until that answers, the session's own list of doors.
  const details = profile?.identityDetails ?? [];
  const steamLinked =
    details.some((d) => d.provider === 'STEAM') || me.identities.includes('STEAM');
  const anyLinked = details.length > 0 || me.identities.some((provider) => provider !== 'PASSWORD');
  // What this account can do here, by its roles, as the rail gates the same pages. A tester's
  // line is the card under these (TesterStart), which an admin never gets: theirs is here.
  const canCreate = me.roles.some((r) => r === 'EDITOR' || r === 'CURATOR' || r === 'ADMIN');
  const canTest = me.roles.includes('ADMIN');

  return (
    <main className="page narrow">
      <header className="topbar">
        <Link to="/" className="wordmark small" aria-label={meta.name}>
          <Wordmark name={meta.name} small />
        </Link>
        <h1>{ca.welcomeTitle(me.displayName || me.handle || '')}</h1>
      </header>
      <p>{ca.welcomeIntro}</p>
      <ul className="welcome-can" aria-label={ca.welcomeCan}>
        <li>
          <i className="bi bi-play-circle" aria-hidden="true" /> {ca.welcomeCanSessions}
        </li>
        {canCreate && (
          <li>
            <i className="bi bi-plus-lg" aria-hidden="true" /> {ca.welcomeCanCreate}
          </li>
        )}
        {canTest && (
          <li>
            <i className="bi bi-controller" aria-hidden="true" /> {ca.welcomeCanTests}
          </li>
        )}
      </ul>
      <TesterStart where="welcome" steamLinked={steamLinked} />
      {!steamLinked && (
        <p className="muted small">
          <i className="bi bi-steam" aria-hidden="true" /> {ca.welcomeSteamRecommended}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <section className="card">
        <IdentityPanel
          details={details}
          steamEnabled={meta.features.steam}
          discordEnabled={meta.features.discord}
          next={`/welcome${nextQuery(next)}`}
          notices={{ steam: search.get('steam'), discord: search.get('discord') }}
          onClaim={(provider, subject) =>
            claimIdentity(provider, subject)
              .then((p) => {
                setProfile(p);
                setError(null);
              })
              .catch((e) => setError(errorText(e)))
          }
          onRemove={(provider) =>
            removeIdentity(provider)
              .then((p) => {
                setProfile(p);
                setError(null);
              })
              .catch((e) => setError(errorText(e)))
          }
        />
      </section>
      <div className="row actions">
        <button type="button" className="primary" onClick={() => void done()}>
          {anyLinked ? ca.welcomeContinue : ca.welcomeSkip}
        </button>
      </div>
    </main>
  );
}
